import {
	Box,
	FormControlLabel,
	Grid,
	InputAdornment,
	Radio,
	RadioGroup,
} from "@mui/material";
import { useEffect, useState } from "react";

import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ParametersEdit } from "../../features/cargo/ParametersEdit";
import { useCargoQuickOptions } from "../../hooks/useCargoQuickOptions";
import SetValueButton from "../SetValueButton";
import { ClearNoteButton } from "../clear-note-button";
import ControlledSelect from "../controlled/ControlledSelect";
import ControlledTextField from "../controlled/ControlledTextField";

export const ParametersPart = ({
	control,
	errors,
	setValue,
	getValues,
	watch,
	settings,
}) => {
	const { t } = useTranslation();
	const vehicleTypesOptions = settings.vehicleOptions.vehicleTypesOptions;
	const {
		weightOptions: weightQuickOptions,
		lengthOptions: lengthQuickOptions,
		vehicleTypeOptions: vehicleTypeQuickOptions,
		vehicleBodyOptions: vehicleBodyQuickOptions,
		additionalRequirementsOptions: additionalRequirementsQuickOptions,
		isUpdating: isUpdatingQuickOptions,
	} = useCargoQuickOptions();

	const handleAppendValue = (target, value) => {
		const currentValues = getValues(target) || [];

		// if value is already in the list, remove it
		if (currentValues.includes(value)) {
			const newValues = currentValues.filter((v) => v !== value);
			setValue(target, newValues, { shouldValidate: true });
			return;
		}

		const newValues = [...new Set([...currentValues, value])];
		setValue(target, newValues, { shouldValidate: true });
	};

	const checkIfSelected = (target, value) => {
		const currentValues = getValues(target) || [];

		return currentValues.includes(value);
	};

	const watchVehicleTypeId = watch("vehicleTypeId");
	const watchVehicleBodyId = watch("vehicleBodyId");
	const [vehicleBodyIdOptions, setVehicleBodyIdOptions] = useState([]);
	useEffect(() => {
		if (watchVehicleTypeId && watchVehicleTypeId.length > 0) {
			if (watchVehicleTypeId.length > 1) {
				setVehicleBodyIdOptions(vehicleTypesOptions["ANY_TYPE"].bodyTypes);
			} else {
				setVehicleBodyIdOptions(
					vehicleTypesOptions[watchVehicleTypeId].bodyTypes
				);
			}

			if (watchVehicleBodyId) {
				let isVehicleBodyInOptions = false;

				if (watchVehicleTypeId.length > 1) {
					isVehicleBodyInOptions = watchVehicleBodyId.every((id) => {
						return Object.keys(
							vehicleTypesOptions["ANY_TYPE"].bodyTypes
						).includes(id);
					});
				} else {
					isVehicleBodyInOptions = watchVehicleBodyId.every((id) => {
						return Object.keys(
							vehicleTypesOptions[watchVehicleTypeId].bodyTypes
						).includes(id);
					});
				}

				if (!isVehicleBodyInOptions) {
					setValue("vehicleBodyId", []);
				}
			}
		}
	}, [setValue, vehicleTypesOptions, watchVehicleBodyId, watchVehicleTypeId]);

	return (
		<Grid container rowSpacing={0} columnSpacing={1} sx={{ py: 1, px: 2 }}>
			<Box
				sx={{
					position: "absolute",
					top: "-12px",
					left: "269px",
					background: "#fff",
					border: "1px solid #0069b4",
					height: "24px",
					width: "100px",
					display: "flex",
					alignItems: "center",
				}}
			>
				<Controller
					control={control}
					name="isFtl"
					defaultValue="true"
					render={({ field: { onChange, value } }) => (
						<RadioGroup name="isFtl" row value={value} onChange={onChange}>
							<FormControlLabel
								sx={{ ml: "11px", mr: "22px" }}
								value="true"
								control={
									<Radio
										sx={{
											padding: 0,
											"& .MuiSvgIcon-root": {
												fontSize: 16,
											},
										}}
									/>
								}
								label="FTL"
							/>
							<FormControlLabel
								sx={{ mr: 0 }}
								value="false"
								control={
									<Radio
										sx={{
											padding: 0,
											mr: 0,
											"& .MuiSvgIcon-root": {
												fontSize: 16,
											},
										}}
									/>
								}
								label="LTL"
							/>
						</RadioGroup>
					)}
				/>
			</Box>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledTextField
						control={control}
						margin="normal"
						name="weight"
						label={t("weight")}
						defaultValue=""
						error={!!errors?.weight}
						endAdornment={<InputAdornment position="end">t</InputAdornment>}
						required
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledTextField
						control={control}
						margin="normal"
						name="length"
						label={t("length")}
						defaultValue=""
						error={!!errors?.length}
						endAdornment={<InputAdornment position="end">m</InputAdornment>}
						required
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 3,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledSelect
						control={control}
						margin="normal"
						name="vehicleTypeId"
						label={t("vehicle_type_label") + " *"}
						defaultValue={[]}
						multiple
						error={!!errors?.vehicleTypeId}
						options={Object.entries(settings.vehicleOptions.vehicleTypesOptions)
							.filter(([value, _label]) => {
								return value !== "ANY_TYPE";
							})
							.map(([value, label]) => {
								return { value, label: label.name };
							})}
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 3,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledSelect
						disabled={!watchVehicleTypeId || watchVehicleTypeId?.length < 1}
						control={control}
						margin="normal"
						name="vehicleBodyId"
						label={t("vehicle_body_label") + " *"}
						defaultValue={[]}
						multiple
						error={!!errors?.vehicleBodyId}
						options={Object.entries(vehicleBodyIdOptions).map(
							([value, label]) => {
								// allow only 5 options to be selected
								const shouldDisable =
									watchVehicleBodyId &&
									watchVehicleBodyId.length > 4 &&
									!watchVehicleBodyId.includes(value);

								return { value, label, disabled: shouldDisable };
							}
						)}
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						position: "relative",
					}}
				>
					<ControlledTextField
						control={control}
						margin="normal"
						name="price"
						label={t("price")}
						defaultValue=""
						error={!!errors?.price}
						startAdornment={<InputAdornment position="start">€</InputAdornment>}
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box
					sx={{
						position: "relative",
						justifyContent: "center",
						alignItems: "center",
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					{weightQuickOptions.map((value) => (
						<SetValueButton
							key={`quick-opt-weight-${value}`}
							type="button"
							style={{
								backgroundColor:
									getValues("weight") === value && !isUpdatingQuickOptions
										? "#a0cbeb"
										: "#d6eeff",
							}}
							onClick={() =>
								setValue("weight", value, {
									shouldValidate: true,
								})
							}
							isUpdating={isUpdatingQuickOptions}
						>
							{value} t
						</SetValueButton>
					))}
					<ParametersEdit type="weight" />
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box
					sx={{
						position: "relative",
						justifyContent: "center",
						alignItems: "center",
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					{lengthQuickOptions.map((value) => (
						<SetValueButton
							key={`quick-opt-length-${value}`}
							type="button"
							style={{
								backgroundColor:
									getValues("length") === value && !isUpdatingQuickOptions
										? "#a0cbeb"
										: "#d6eeff",
							}}
							onClick={() =>
								setValue("length", value, {
									shouldValidate: true,
								})
							}
							isUpdating={isUpdatingQuickOptions}
						>
							{value} m
						</SetValueButton>
					))}
					<ParametersEdit type="length" />
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 3,
				}}
			>
				<Box
					sx={{
						position: "relative",
						justifyContent: "center",
						alignItems: "center",
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					{vehicleTypeQuickOptions.map((value) => (
						<SetValueButton
							key={`quick-opt-vehicleTypeId-${value}`}
							type="button"
							style={{
								backgroundColor:
									checkIfSelected("vehicleTypeId", value) &&
									!isUpdatingQuickOptions
										? "#a0cbeb"
										: "#d6eeff",
							}}
							onClick={() => handleAppendValue("vehicleTypeId", value)}
							isUpdating={isUpdatingQuickOptions}
						>
							{settings.vehicleOptions.vehicleTypesOptions[value].name}
						</SetValueButton>
					))}
					<ParametersEdit type="vehicleTypeId" />
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 3,
				}}
			>
				<Box
					sx={{
						position: "relative",
						justifyContent: "center",
						alignItems: "center",
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					<SetValueButton
						disabled={
							!watchVehicleTypeId ||
							watchVehicleBodyId?.length > 4 ||
							watchVehicleTypeId?.length < 1 ||
							(!Object.keys(vehicleBodyIdOptions).includes("CURTAIN_SIDER") &&
								!Object.keys(vehicleBodyIdOptions).includes("TAUTLINER"))
						}
						type="button"
						style={{
							backgroundColor:
								checkIfSelected("vehicleBodyId", "CURTAIN_SIDER") &&
								checkIfSelected("vehicleBodyId", "TAUTLINER") &&
								!isUpdatingQuickOptions
									? "#a0cbeb"
									: "#d6eeff",
						}}
						onClick={() => {
							const isCurtainSiderSelected = checkIfSelected(
								"vehicleBodyId",
								"CURTAIN_SIDER"
							);
							const isTautlineSelected = checkIfSelected(
								"vehicleBodyId",
								"TAUTLINER"
							);

							if (!isCurtainSiderSelected) {
								handleAppendValue("vehicleBodyId", "CURTAIN_SIDER");
							}

							if (!isTautlineSelected) {
								handleAppendValue("vehicleBodyId", "TAUTLINER");
							}

							if (isCurtainSiderSelected && isTautlineSelected) {
								handleAppendValue("vehicleBodyId", "CURTAIN_SIDER");
								handleAppendValue("vehicleBodyId", "TAUTLINER");
							}
						}}
						isUpdating={isUpdatingQuickOptions}
					>
						{t("cargo_quick_option_curtain_plus_tautliner")}
					</SetValueButton>
					{vehicleBodyQuickOptions.slice(0, 2).map((value) => (
						<SetValueButton
							key={`quick-opt-vehicleBodyId-${value}`}
							disabled={
								!watchVehicleTypeId ||
								watchVehicleBodyId?.length > 4 ||
								watchVehicleTypeId?.length < 1 ||
								!Object.keys(vehicleBodyIdOptions).includes(value)
							}
							type="button"
							style={{
								backgroundColor:
									checkIfSelected("vehicleBodyId", value) &&
									!isUpdatingQuickOptions
										? "#a0cbeb"
										: "#d6eeff",
							}}
							onClick={() => handleAppendValue("vehicleBodyId", value)}
							isUpdating={isUpdatingQuickOptions}
						>
							{settings.vehicleOptions.vehicleBodyTypesOptions[value]}
						</SetValueButton>
					))}
					<ParametersEdit type="vehicleBodyId" />
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledTextField
						control={control}
						margin="normal"
						name="customerPrice"
						label={t("customer_price")}
						defaultValue=""
						error={!!errors?.customerPrice}
						startAdornment={<InputAdornment position="start">€</InputAdornment>}
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 5,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledSelect
						control={control}
						margin="normal"
						name="additionalRequirements"
						label={t("additional_requirements")}
						defaultValue={[]}
						multiple
						error={!!errors?.additionalRequirements}
						options={Object.entries(
							settings.vehicleOptions.additionalRequirementsOptions
						).map(([value, label]) => {
							const shouldDisable =
								watchVehicleBodyId &&
								watchVehicleBodyId.length > 4 &&
								!watchVehicleBodyId.includes(value);

							return { value, label, disabled: shouldDisable };
						})}
					/>
				</Box>
			</Grid>
			<Grid
				container
				alignItems="flex-end"
				size={{
					lg: 5,
				}}
			>
				<Box
					sx={{
						position: "relative",
						width: "100%",
						display: "flex",
						flexWrap: "wrap",
						rowGap: "4px",
						columnGap: "4px",
						mb: "4px",
					}}
				>
					{additionalRequirementsQuickOptions.map((value, index) => (
						<Box
							key={`quick-opt-additionalRequirements-${value}`}
							sx={{
								width:
									index === additionalRequirementsQuickOptions.length - 1
										? "calc(66.6666%)"
										: `calc(33.3333% ${index === 2 ? "" : " - 4px"})`,
							}}
						>
							<SetValueButton
								type="button"
								style={{
									backgroundColor:
										checkIfSelected("additionalRequirements", value) &&
										!isUpdatingQuickOptions
											? "#a0cbeb"
											: "#d6eeff",
								}}
								onClick={() =>
									handleAppendValue("additionalRequirements", value)
								}
								isUpdating={isUpdatingQuickOptions}
							>
								{settings.vehicleOptions.additionalRequirementsOptions[value]}
							</SetValueButton>
						</Box>
					))}
					<ParametersEdit type="additionalRequirements" />
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 2,
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<ControlledTextField
						control={control}
						margin="normal"
						name="payment"
						label={t("payment")}
						defaultValue=""
						error={!!errors?.payment}
						endAdornment={
							<InputAdornment position="end">{t("days")}</InputAdornment>
						}
					/>
				</Box>
			</Grid>
			<Grid
				size={{
					lg: 10,
				}}
			>
				<Box
					sx={{ alignItems: "center", display: "flex", position: "relative" }}
				>
					<ControlledTextField
						control={control}
						margin="normal"
						name="publicNote"
						label={t("public_note_label")}
						defaultValue=""
						error={!!errors?.publicNote}
						multiline={true}
						maxRows={1}
					/>
					<Box sx={{ position: "absolute", right: "8px", top: "4px" }}>
						<ClearNoteButton
							action={() =>
								setValue("publicNote", "", {
									shouldValidate: true,
									shouldDirty: true,
								})
							}
						/>
					</Box>
				</Box>
			</Grid>
		</Grid>
	);
};
